import React, { useState, useEffect, FC, useRef } from "react";
import { Icon } from "../../components/Component";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Button } from "reactstrap";
import { Modal } from "antd";
import { convertNumberToWords } from "../../utils/Utils";
import dayjs from "dayjs";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface BuyerDetails {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  gstin: string;
  pan: string;
  cin: string;
  phoneNumber?: string;
}

interface SupplierDetails {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  gstin: string;
  pan: string;
  cin: string;
  phoneNumber?: string;
}

const PurchaseReturnPDF: FC<any> = ({
  buyerDetails,
  supplierDetails,
  returnData,
  buyerOrgLogo,
  hasIGST,
  TermsAndConditions,
  getPdfUrl,
  returnApprovals,
}: any) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(`${url}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            resolve(reader.result as string);
          } else {
            reject(new Error("Failed to read the image data"));
          }
        };
        reader.onerror = () =>
          reject(new Error("Error reading the image data"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching the image:", error);
      throw new Error("Failed to fetch the image.");
    }
  };

  const drawBorderedBox = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 5;
    const boxX = margin;
    const boxY = 20;
    const boxWidth = pageWidth - 2 * margin;
    const boxHeight = pageHeight - 41;
    doc.setDrawColor(157, 157, 157);
    doc.setLineWidth(0.2);
    doc.rect(boxX, boxY, boxWidth, boxHeight);
  };

  const generatePDF = async (download?: boolean) => {
    const doc = new jsPDF();

    const addImageWithMaxHeight = async (
      doc: any,
      imageData: string,
      x: number,
      y: number,
      maxHeight: number
    ) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = imageData;

        img.onload = function () {
          const aspectRatio = img.width / img.height;
          const height = Math.min(img.height, maxHeight);
          const width = height * aspectRatio;
          doc.addImage(img.src, "PNG", x, y, width, height);
          resolve();
        };

        img.onerror = function (error) {
          console.error("Image failed to load", error);
          reject(new Error("Image load failed"));
        };
      });
    };

    const addHeader = async (doc: jsPDF, leftLogo: string | null) => {
      if (leftLogo) {
        await addImageWithMaxHeight(doc, leftLogo, 10, 5, 10);
      } else {
        doc.setFontSize(20);
        doc.text("Procurex", 10, 20);
      }
    };

    const addTable = (doc: any, returnData: any, currentY: any) => {
      const currency = "Rs.";

      const mainTitles: any = [
        { content: "Sno", rowSpan: 2 },
        { content: "Item & Description", rowSpan: 2 },
        { content: "Category", rowSpan: 2 },
        { content: "Invoiced Qty", rowSpan: 2 },
        { content: "Received Qty", rowSpan: 2 },
        { content: "Return Qty", rowSpan: 2 },
        { content: `Rate (${currency})`, rowSpan: 2 },
      ];

      if (hasIGST) {
        mainTitles.push({ content: "IGST", colSpan: 2 });
        mainTitles.push({ content: `Line Total (${currency})`, rowSpan: 2 });
      } else {
        mainTitles.push({ content: "CGST", colSpan: 2 });
        mainTitles.push({ content: "SGST", colSpan: 2 });
        mainTitles.push({ content: `Line Total (${currency})`, rowSpan: 2 });
      }

      const subTitles = [{ content: "%" }, { content: "Amount" }];
      if (!hasIGST) {
        subTitles.push({ content: "%" }, { content: "Amount" });
      }
      const head = [mainTitles, subTitles];

      const formatCurrencyValue = (value: any) => {
        if (!value && value !== 0) return "0.00";
        return Number(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      };

      const rows = returnData?.returnLines?.map((line: any, index: any) => {
        const row = [
          (index + 1).toString(),
          line.materialName || line.itemDescription || "-",
          line.category || "-",
          Number(line.invoicedQuantity || 0).toFixed(2),
          Number(line.deliveredQuantity || line.receivedQuantity || 0).toFixed(2),
          Number(line.returnQuantity || 0).toFixed(2),
          formatCurrencyValue(line.unitPrice),
        ];

        if (hasIGST) {
          row.push(
            Number(line.igstPercentage || 0).toFixed(2),
            formatCurrencyValue(line.igst || line.igstAmount)
          );
        } else {
          row.push(
            Number(line.cgstPercentage || 0).toFixed(2),
            formatCurrencyValue(line.cgst || line.cgstAmount),
            Number(line.sgstPercentage || 0).toFixed(2),
            formatCurrencyValue(line.sgst || line.sgstAmount)
          );
        }

        row.push(formatCurrencyValue(line.grossTotal));
        return row;
      });

      let footer: any = [];

      if (hasIGST) {
        footer = [
          [
            "Total In Words",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "Taxable Amount:", colSpan: 2 },
            formatCurrencyValue(returnData?.netAmount || 0),
            "",
          ],
          [
            {
              content: "Indian Rupee " + convertNumberToWords(
                String(Number(returnData?.grossAmount || 0).toFixed(2)).replace(/,/g, "")
              ),
              colSpan: 7,
            },
            "",
            { content: "IGST:", colSpan: 2 },
            formatCurrencyValue(returnData?.igstAmount || 0),
            "",
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "Total:", colSpan: 2 },
            formatCurrencyValue(returnData?.grossAmount || 0),
            "",
          ],
          ["", "", "", "", "", "", "", "", "", "", ""],
          ["", "", "", "", "", "", "", "", "", "", ""],
        ];
      } else {
        footer = [
          [
            "Total In Words",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "Taxable Amount:", colSpan: 2 },
            formatCurrencyValue(returnData?.netAmount || 0),
            "",
          ],
          [
            {
              content: "Indian Rupee " + convertNumberToWords(
                String(Number(returnData?.grossAmount || 0).toFixed(2)).replace(/,/g, "")
              ),
              colSpan: 7,
            },
            "",
            "",
            { content: "CGST:", colSpan: 2 },
            formatCurrencyValue(returnData?.cgstAmount || 0),
            "",
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "SGST:", colSpan: 2 },
            formatCurrencyValue(returnData?.sgstAmount || 0),
            "",
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "Total:", colSpan: 2 },
            formatCurrencyValue(returnData?.grossAmount || 0),
            "",
          ],
          ["", "", "", "", "", "", "", "", "", "", ""],
          ["", "", "", "", "", "", "", "", "", "", ""],
        ];
      }

      doc.autoTable({
        head,
        body: rows,
        foot: footer,
        startY: currentY,
        pageBreak: "auto",
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          overflow: "linebreak",
        },
        headStyles: {
          fontSize: 8,
          fillColor: [227, 227, 227],
          textColor: [40, 40, 40],
          valign: "middle",
          lineWidth: 0.2,
          lineColor: [180, 180, 180],
        },
        footStyles: {
          fontSize: 8,
          textColor: 0,
          fillColor: false,
          lineWidth: 0,
        },
        theme: "grid",
        margin: { top: 20, bottom: 20, left: 5.1, right: 5.1 },
        showFoot: "lastPage",
      });
    };

    const drawSignatureBox = async (doc: jsPDF, startY: number) => {
      const pageWidth = doc.internal.pageSize.getWidth();

      const boxWidth = 60;
      const boxHeight = 25;

      const x = pageWidth - boxWidth - 10;
      const y = startY + 10;

      doc.setDrawColor(0);
      doc.setLineWidth(0.3);
      doc.rect(x, y, boxWidth, boxHeight);

      let signaturePath = null;

      // approvals exist 
      if (!returnApprovals || returnApprovals.length === 0) {
        // Auto Approved 
        if (Array.isArray(returnData?.dms) && returnData.dms.length > 0) {
          signaturePath = returnData.dms[0]?.filePath;
        }
      }

      if (signaturePath) {
        try {
          const imgBase64 = await fetchImageAsBase64(signaturePath);

          doc.addImage(
            imgBase64,
            "PNG",
            x + 5,
            y + 3,
            boxWidth - 10,
            12
          );
        } catch (e) {
          console.error("Signature load failed", e);
        }
      }

      // label always
      doc.setFontSize(9);
      doc.text(
        returnData?.signatureLabel || "Authorised Signature",
        x + boxWidth / 2,
        y + boxHeight - 5,
        { align: "center" }
      );
    };

    const breakLongText = (text: any, maxLineLength = 30) => {
      if (!text) return " ";
      return text.match(new RegExp(`.{1,${maxLineLength}}`, "g"))?.join("\n") || text;
    };

    const addFooter = async (
      doc: jsPDF,
      pageNum: number,
      totalPages: number
    ) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(7);

      const line1 = `${buyerDetails.name}`;
      const line2 = [
        buyerDetails.addressLine1,
        buyerDetails.addressLine2,
        buyerDetails.city,
        buyerDetails.state,
        buyerDetails.pincode,
        buyerDetails.country
      ].filter(Boolean).join(", ");
      const line3 = `CIN: ${buyerDetails.cin} `;
      const line4 = `GSTIN: ${buyerDetails.gstin} | PAN: ${buyerDetails.pan}`;

      const lastLineY = pageHeight - 7;
      const lineSpacing = 3;
      const startY = lastLineY - 3 * lineSpacing;

      doc.setFontSize(8);
      const pageText = `Page ${pageNum} of ${totalPages}`;
      doc.text(pageText, pageWidth - 20, pageHeight - 10, { align: "right" });

      doc.setFontSize(7);
      doc.text(line1, pageWidth / 2, startY, { align: "center" });
      doc.text(line2, pageWidth / 2, startY + lineSpacing, { align: "center" });
      doc.text(line3, pageWidth / 2, startY + 2 * lineSpacing, {
        align: "center",
      });
      doc.text(line4, pageWidth / 2, startY + 3 * lineSpacing, {
        align: "center",
      });
    };

    const addGeneralDetailsSection = async (
      doc: jsPDF,
      buyerDetails: BuyerDetails,
      returnData: any,
      supplierDetails: SupplierDetails
    ) => {
      const sectionY = 16;
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);

      let currentY = sectionY + 10;

      // Add buyer details
      if (buyerDetails) {
        const lineHeight = 4;
        doc.text(buyerDetails.name || "", 10, currentY);
        currentY += 5;

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        const address1 = breakLongText(buyerDetails.addressLine1 || "");
        doc.text(address1, 10, currentY);
        currentY += address1.split("\n").length * lineHeight;

        const address2 = breakLongText(buyerDetails.addressLine2 || "");
        doc.text(address2, 10, currentY);
        currentY += address2.split("\n").length * lineHeight;

        doc.text(
          [
            buyerDetails.city,
            buyerDetails.state,
            buyerDetails.country,
            buyerDetails.pincode
          ]
            .filter(Boolean)
            .join(", "),
          10,
          currentY
        );
        currentY += 5;

        const gstin = breakLongText(`GSTIN: ${buyerDetails.gstin || ""}`);
        doc.text(gstin, 10, currentY);
        currentY += gstin.split("\n").length * lineHeight;

        const pan = breakLongText(`PAN: ${buyerDetails.pan || ""}`);
        doc.text(pan, 10, currentY);
        currentY += pan.split("\n").length * lineHeight;

        const cin = breakLongText(`CIN: ${buyerDetails.cin || ""}`);
        doc.text(cin, 10, currentY);
        currentY += cin.split("\n").length * lineHeight;

        const phone = breakLongText(`Phone: ${buyerDetails.phoneNumber || ""}`);
        doc.text(phone, 10, currentY);
        currentY += phone.split("\n").length * lineHeight;

        currentY += 5;
      }

      // Add "PURCHASE RETURN" text on the right
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      const purchaseReturnText = "PURCHASE RETURN";
      const textWidth = doc.getTextWidth(purchaseReturnText);
      const rightX = pageWidth - textWidth - 10;
      doc.setTextColor(20, 20, 20);
      doc.text(purchaseReturnText, rightX, sectionY + 40);

      // Draw horizontal line
      doc.setDrawColor(150, 150, 150);
      doc.line(5, currentY, pageWidth - 5, currentY);
      currentY += 5;

      // Add Return details tables
      const sectionWidth = (pageWidth - 10) / 2;
      let leftTableHeight = currentY;
      let rightTableHeight = currentY;
      const tableStartY = currentY;

      // Left table - Return details
      doc.autoTable({
        startY: currentY,
        theme: "plain",
        showHead: "never",
        body: [
          ["Return ID:", returnData?.returnId || `RTN/${returnData?.returnNumber}` || " "],
          ["Return Date:", returnData?.returnDate ? dayjs(returnData.returnDate).format("DD-MMM-YYYY") : " "],
          ["Return Type:", returnData?.returnType || " "],
          ["Order Number:", returnData?.orderId || returnData?.orderNumber || " "],
          ["Invoice Number:", returnData?.invoiceId || returnData?.invoiceNumber || " "],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        margin: { left: 10 },
        didDrawCell: (data: any) => {
          leftTableHeight = Math.max(
            leftTableHeight,
            data.cell.y + data.cell.height
          );
        },
      });

      // Right table - Supplier and Site details
      doc.autoTable({
        startY: currentY,
        margin: { left: sectionWidth + 10 },
        theme: "plain",
        showHead: "never",
        body: [
          ["Supplier:", returnData?.supplierName || " "],
          ["Site:", returnData?.siteName || " "],
          ["Created By:", (returnData?.createdUserName || "NA")],
          ["Created Date:", returnData?.createdDate ? dayjs(returnData.createdDate).format("DD-MMM-YYYY") : "N/A"],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        didDrawCell: (data: any) => {
          rightTableHeight = Math.max(
            rightTableHeight,
            data.cell.y + data.cell.height
          );
        },
      });

      const maxHeight = Math.max(leftTableHeight, rightTableHeight);
      doc.setDrawColor(150, 150, 150);
      doc.line(sectionWidth + 5, tableStartY, sectionWidth + 5, maxHeight + 5);

      // Add Supplier Details section
      currentY = maxHeight + 10;
      doc.setFillColor(200, 200, 200);
      doc.rect(5, currentY, pageWidth - 10, 6, "F");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text("Supplier Details", 10, currentY + 4);
      currentY += 12;

      if (supplierDetails) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(supplierDetails.name || "", 10, currentY);
        currentY += 5;

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(
          [supplierDetails.addressLine1, supplierDetails.addressLine2]
            ?.filter(Boolean)
            ?.join(", "),
          10,
          currentY
        );
        currentY += 5;
        doc.text(
          [
            supplierDetails?.city,
            supplierDetails?.state,
            supplierDetails?.country,
            supplierDetails?.pincode,
          ]
            ?.filter(Boolean)
            ?.join(", "),
          10,
          currentY
        );
        currentY += 5;
        doc.text(`CIN: ${supplierDetails.cin || ""}`, 10, currentY);
        currentY += 5;
        doc.text(`GSTIN: ${supplierDetails.gstin || ""}`, 10, currentY);
        currentY += 5;
        doc.text(`PAN: ${supplierDetails.pan || ""}`, 10, currentY);
        currentY += 5;
        doc.text(`Phone: ${supplierDetails.phoneNumber || ""}`, 10, currentY);
        currentY += 5;
      }

      return currentY;
    };

    const addApprovalsSection = async (doc: jsPDF, approvals: any[]) => {
      if (!approvals || approvals.length === 0) {
        return;
      }

      doc.addPage();
      const pageWidth = doc.internal.pageSize.getWidth();
      const borderY = 20;
      let currentY = borderY + 6;

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Approvals", pageWidth / 2, currentY, { align: "center" });

      currentY += 8;

      const formatDateTime = (dateString: string | null) => {
        if (!dateString) return "";
        try {
          let date: Date;
          if (typeof dateString === "string" && !dateString.includes("T") && !dateString.includes(" ")) {
            const parts = dateString.split("-");
            if (parts.length === 3) {
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const day = parseInt(parts[2], 10);
              date = new Date(year, month, day, 12, 0, 0);
            } else {
              date = new Date(dateString);
            }
          } else {
            date = new Date(dateString);
          }

          if (isNaN(date.getTime())) return "";
          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
          return formattedDate;
        } catch (error) {
          return "";
        }
      };

      const approvalRowsData = await Promise.all(
        approvals.map(async (approval: any) => {
          let statusText = "";
          const status = (approval.status || "").toUpperCase();
          if (status === "PENDING") {
            statusText = "Pending";
          } else if (status === "APPROVED") {
            const approvedDateTime = formatDateTime(approval.actionDate || approval.approvalDate);
            statusText = approvedDateTime ? `Approved\n${approvedDateTime}` : "Approved";
          } else if (status === "REJECTED") {
            const rejectedDateTime = formatDateTime(approval.actionDate || approval.approvalDate);
            statusText = rejectedDateTime ? `Rejected\n${rejectedDateTime}` : "Rejected";
          } else {
            statusText = approval.status || "";
          }

          let signatureImage: string | null = null;
          let signaturePath = null;

          if (Array.isArray(approval.dms) && approval.dms.length > 0) {
            signaturePath = approval.dms[0]?.filePath;
          } else if (approval.dms?.filePath) {
            signaturePath = approval.dms.filePath;
          }

          if (signaturePath) {
            try {
              signatureImage = await fetchImageAsBase64(signaturePath);
            } catch (error) {
              console.error("Error fetching signature image:", error);
              signatureImage = null;
            }
          }

          return {
            name: approval.userName || "",
            role: approval.roleName || "",
            level: approval.level || approval.levelName || "",
            status: statusText,
            signature: signatureImage,
          };
        })
      );

      const approvalRows = approvalRowsData.map((row: any) => [
        row.name,
        row.role,
        row.level,
        row.status,
        row.signature ? "" : "",
      ]);

      const availableWidth = pageWidth - 20;

      doc.autoTable({
        head: [["Name", "Role", "Level", "Status", "Signature"]],
        body: approvalRows,
        startY: currentY,
        margin: { top: 0, left: 10, right: 10, bottom: 10 },
        theme: "grid",
        headStyles: {
          fontSize: 10,
          fillColor: false,
          textColor: 0,
          valign: "middle",
          halign: "center",
        },
        styles: {
          fontSize: 9,
          lineWidth: 0.2,
          cellPadding: 4,
          overflow: "linebreak",
          valign: "middle",
        },
        columnStyles: {
          0: { cellWidth: availableWidth * 0.2, halign: "left" },
          1: { cellWidth: availableWidth * 0.2, halign: "left" },
          2: { cellWidth: availableWidth * 0.12, halign: "left" },
          3: { cellWidth: availableWidth * 0.28, halign: "left" },
          4: { cellWidth: availableWidth * 0.2, halign: "center" },
          8: { lineWidth: { left: 0.5, right: 0.2 } },
          9: { lineWidth: { right: 0.5 } },
          10: { lineWidth: { left: 0.5, right: 0.2 } },
          11: { lineWidth: { right: 0.5 } },
        },
        didDrawCell: (data: any) => {
          if (data.column.index === 4 && data.section === "body") {
            const rowIndex = data.row.index;
            const signatureData = approvalRowsData[rowIndex];
            if (signatureData?.signature) {
              try {
                const cellPadding = 4;
                const imgWidth = Math.min(35, data.cell.width - cellPadding * 2);
                const imgHeight = Math.min(15, data.cell.height - cellPadding * 2);
                const x = data.cell.x + (data.cell.width - imgWidth) / 2;
                const y = data.cell.y + cellPadding + (data.cell.height - cellPadding * 2 - imgHeight) / 2;
                doc.addImage(signatureData.signature, "PNG", x, y, imgWidth, imgHeight);
              } catch (error) {
                console.error("Error adding signature image:", error);
              }
            }
          }
        },
        didParseCell: (data: any) => {
          if (data.section === "head" && data.row.index === 0) {
            if (data.cell.raw?.content === "CGST" || data.cell.raw?.content === "SGST") {
              data.cell.styles.lineWidth = 0.5;
              data.cell.styles.lineColor = [0, 0, 0];
            }
          }
        },
      });
    };

    const addTermsAndConditions = (doc: jsPDF) => {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Terms and Conditions", 10, 30);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      const termsAndConditionsContent: any = TermsAndConditions || "";
      const maxLineWidth = 180;
      const termsAndConditions = termsAndConditionsContent.split("\n");

      let currentY = 40;
      const lineHeight = 4.5;
      const pageHeight = doc.internal.pageSize.getHeight();

      termsAndConditions.forEach((point: any) => {
        const wrappedText = doc.splitTextToSize(point, maxLineWidth);
        const requiredHeight = wrappedText.length * lineHeight;

        if (currentY + requiredHeight + 30 > pageHeight) {
          doc.addPage();
          currentY = 30;
        }

        doc.text(wrappedText, 10, currentY);
        currentY += requiredHeight;
      });
    };

    // Generate PDF

    let leftLogo: string | null = null;

    if (buyerOrgLogo) {
      try {
        leftLogo = await fetchImageAsBase64(buyerOrgLogo);
      } catch (error) {
        console.error("Buyer logo load failed", error);
        leftLogo = null;
      }
    }
    const currentY = await addGeneralDetailsSection(
      doc,
      buyerDetails,
      returnData,
      supplierDetails
    );

    addTable(doc, returnData, currentY);

    const finalY = (doc as any).lastAutoTable?.finalY || currentY;

    // draw signature box separately
    await drawSignatureBox(doc, finalY);

    if (returnApprovals && returnApprovals.length > 0) {
      await addApprovalsSection(doc, returnApprovals);
    }

    const applyHeaderAndFooter = async () => {
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await addHeader(doc, leftLogo);
        await addFooter(doc, i, totalPages);
        drawBorderedBox(doc);
      }
    };

    if (TermsAndConditions && TermsAndConditions.trim() !== "") {
      doc.addPage();
      addTermsAndConditions(doc);
    }
    await applyHeaderAndFooter();

    const finalPageCount = (doc as any).internal.getNumberOfPages();
    if (finalPageCount > 0) {
      doc.setPage(1);
    }

    if (download) {
      try {
        const pdfBlob = doc.output("blob");
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        const fileName = `PR_${returnData?.orderNumber || "PR"}-${returnData?.returnNumber || ""}.pdf`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error during PDF download:", error);
        try {
          doc.save((returnData?.returnId || "Return") + ".pdf");
        } catch (saveError) {
          console.error("Error with doc.save():", saveError);
          alert("Failed to download PDF. Please try again.");
        }
      }
      return;
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    return { pdfUrl, pdfBlob };
  };

  // Call generatePDF on mount to get the blob reference
  useEffect(() => {
    let isMounted = true;

    const initPDF = async () => {
      try {
        const result = await generatePDF();
        if (isMounted && result?.pdfBlob && getPdfUrl) {
          getPdfUrl(result.pdfBlob);
        }
      } catch (error) {
        console.log(error);
      }
    };

    initPDF();

    return () => {
      isMounted = false;
    };
  }, [buyerDetails, supplierDetails, returnData]);

  const downloadPDF = async () => {
    try {
      await generatePDF(true);
    } catch (error) {
      console.error("Error in downloadPDF:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  return (
    <>
      <Button
        type="button"
        color=""
        onClick={async () => {
          const { pdfUrl: _pdfUrl }: any = await generatePDF();
          setPdfUrl(_pdfUrl);
        }}
      >
        <Icon name="file-pdf" style={{ marginRight: 1 }} />
        <span>PDF</span>
      </Button>
      {pdfUrl && (
        <Modal
          open={pdfUrl !== null}
          onCancel={() => setPdfUrl(null)}
          width={"90vw"}
          footer={null}
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Purchase Return</span>
              <span
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  downloadPDF().catch((error) => {
                    console.error("Download error:", error);
                  });
                }}
              >
                Download
                <Icon name="download" className="me-4" />
              </span>
            </div>
          }
        >
          <iframe
            src={pdfUrl + "#toolbar=0"}
            referrerPolicy="no-referrer"
            style={{
              width: "100%",
              height: "80vh",
            }}
            title="PDF Viewer"
          ></iframe>
        </Modal>
      )}
    </>
  );
};

export default PurchaseReturnPDF;